/**
 * useWorkflows Hook
 *
 * React Query hooks for workflow CRUD operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workflowsApi } from '../services';
import type {
  WorkflowResponse,
  WorkflowListResponse,
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  WorkflowQuery,
} from '../types';
import type { WorkflowImportRequest, WorkflowImportResult } from '@troia-v3/types';

// Query keys
export const workflowKeys = {
  all: ['workflows'] as const,
  lists: () => [...workflowKeys.all, 'list'] as const,
  list: (filters: WorkflowQuery) => [...workflowKeys.lists(), filters] as const,
  details: () => [...workflowKeys.all, 'detail'] as const,
  detail: (id: string) => [...workflowKeys.details(), id] as const,
  count: (filters?: { status?: string }) => [...workflowKeys.all, 'count', filters] as const,
};

/**
 * Atualização da listagem com contadores (D19): com a página aberta e parada,
 * o número precisa mudar sozinho. Só `staleTime` não bastaria — nada dispararia
 * a busca.
 */
export const WORKFLOWS_STATS_REFRESH_MS = 30_000;

/** Tempo das demais consultas de workflow (o comportamento de sempre). */
const WORKFLOWS_DEFAULT_STALE_MS = 5 * 60 * 1000;

export interface WorkflowsQueryTiming {
  staleTime: number;
  refetchInterval?: number;
  refetchIntervalInBackground?: boolean;
}

/**
 * Tempo de cache/atualização de UMA consulta da listagem, decidido pelo próprio
 * `query` (M10): `withStats` já faz parte da `queryKey`, então acoplar tempo e
 * chave no mesmo lugar impede que alguém ligue um sem o outro.
 *
 * Sem `withStats`, a chave `refetchInterval` NEM EXISTE no objeto — skills e
 * pré-condições de agente seguem com os 5 minutos de hoje.
 */
export function workflowsQueryTiming(query?: WorkflowQuery): WorkflowsQueryTiming {
  if (query?.withStats === true) {
    return {
      staleTime: WORKFLOWS_STATS_REFRESH_MS,
      refetchInterval: WORKFLOWS_STATS_REFRESH_MS,
      // Aba escondida não gasta requisição; ao voltar, o próximo tique traz o número.
      refetchIntervalInBackground: false,
    };
  }
  return { staleTime: WORKFLOWS_DEFAULT_STALE_MS };
}

/**
 * Hook to fetch workflow list with pagination and filters
 */
export function useWorkflows(query?: WorkflowQuery) {
  return useQuery<WorkflowListResponse, Error>({
    queryKey: workflowKeys.list(query || {}),
    queryFn: () => workflowsApi.list(query),
    ...workflowsQueryTiming(query),
  });
}

/**
 * Hook to fetch single workflow by ID
 */
export function useWorkflow(id: string | undefined) {
  return useQuery<WorkflowResponse, Error>({
    queryKey: workflowKeys.detail(id!),
    queryFn: () => workflowsApi.getById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to get workflow count
 */
export function useWorkflowCount(filters?: { status?: string }) {
  return useQuery({
    queryKey: workflowKeys.count(filters),
    queryFn: () => workflowsApi.getCount(filters),
    staleTime: 1 * 60 * 1000,
  });
}

/**
 * Hook to create a new workflow
 */
export function useCreateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowResponse, Error, CreateWorkflowRequest>({
    mutationFn: (data) => workflowsApi.create(data),
    onSuccess: () => {
      // Invalidate all workflow lists
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.count() });
    },
  });
}

/**
 * Hook to update a workflow
 */
export function useUpdateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowResponse, Error, { id: string; data: UpdateWorkflowRequest }>({
    mutationFn: ({ id, data }) => workflowsApi.update(id, data),
    onSuccess: (updatedWorkflow, { data }) => {
      // Update the specific workflow in cache
      queryClient.setQueryData(
        workflowKeys.detail(updatedWorkflow.id),
        updatedWorkflow
      );
      // Invalidate lists to reflect changes
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });

      // If folderId was changed, invalidate folder queries to update workflowCount
      if ('folderId' in data) {
        queryClient.invalidateQueries({ queryKey: ['workflow-folders'] });
      }
    },
  });
}

/**
 * Hook to delete a workflow
 */
export function useDeleteWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id) => workflowsApi.delete(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: workflowKeys.detail(deletedId) });
      // Invalidate lists and count
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.count() });
    },
  });
}

/**
 * Hook to activate a workflow
 */
export function useActivateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowResponse, Error, string>({
    mutationFn: (id) => workflowsApi.activate(id),
    onSuccess: (updatedWorkflow) => {
      queryClient.setQueryData(
        workflowKeys.detail(updatedWorkflow.id),
        updatedWorkflow
      );
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.count() });
    },
  });
}

/**
 * Hook to pause a workflow
 */
export function usePauseWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowResponse, Error, string>({
    mutationFn: (id) => workflowsApi.pause(id),
    onSuccess: (updatedWorkflow) => {
      queryClient.setQueryData(
        workflowKeys.detail(updatedWorkflow.id),
        updatedWorkflow
      );
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.count() });
    },
  });
}

/**
 * Hook to archive a workflow
 */
export function useArchiveWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowResponse, Error, string>({
    mutationFn: (id) => workflowsApi.archive(id),
    onSuccess: (updatedWorkflow) => {
      queryClient.setQueryData(
        workflowKeys.detail(updatedWorkflow.id),
        updatedWorkflow
      );
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.count() });
    },
  });
}

/**
 * Hook to duplicate a workflow
 */
export function useDuplicateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowResponse, Error, { id: string; newName?: string }>({
    mutationFn: ({ id, newName }) => workflowsApi.duplicate(id, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.count() });
    },
  });
}

/**
 * Hook to import a workflow (or bundle) previously exported via workflowsApi.exportOne/exportAll
 */
export function useImportWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<WorkflowImportResult, Error, WorkflowImportRequest>({
    mutationFn: (request) => workflowsApi.importWorkflow(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.count() });
      // Bundle importado cria PASTAS também — sem invalidar, a folderbar fica
      // 5min (staleTime) sem mostrar as pastas novas e os itens dentro delas
      // "somem" (a listagem raiz filtra folderId null). Key literal pra não
      // criar import circular com useWorkflowFolders (que importa workflowKeys
      // deste arquivo).
      queryClient.invalidateQueries({ queryKey: ['workflow-folders'] });
    },
  });
}
